import React, { useState, useCallback } from "react";
import { Input, Label, Badge } from "lift-ui";
import { X } from "lucide-react";

export interface TagInputConfig {
  /** Current tag values */
  value: string[];
  /** Called when tags change (add or remove) */
  onChange: (tags: string[]) => void;
  /** Placeholder for the type-to-add input */
  placeholder?: string;
  /** Disable the input and remove buttons */
  disabled?: boolean;
  /** Input/label id for accessibility */
  id?: string;
  /** Label text shown above the field */
  label?: string;
  /** Optional: called when a tag is added (e.g. for logging) */
  onTagAdd?: (tag: string) => void | Promise<void>;
  /** Optional: called when a tag is removed (e.g. for logging) */
  onTagRemove?: (tag: string) => void | Promise<void>;
  /** Optional class name for the wrapper */
  className?: string;
  /** When true, keeps tags in a single line with horizontal scroll instead of wrapping */
  singleLine?: boolean;
}

/**
 * Tag input component built with lift-ui Input and Badge.
 * Type a value and press Enter to add a tag; click X on a tag to remove it.
 */
export function TagInput({
  value = [],
  onChange,
  placeholder = "Type and Enter (tag value)",
  disabled = false,
  id,
  label,
  onTagAdd,
  onTagRemove,
  className = "",
  singleLine = false,
}: TagInputConfig) {
  const [inputValue, setInputValue] = useState("");

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" || disabled) return;
      const trimmed = inputValue.trim();
      if (!trimmed) return;
      e.preventDefault();
      const newTags = [...value, trimmed];
      onChange(newTags);
      setInputValue("");
      await onTagAdd?.(trimmed);
    },
    [inputValue, value, onChange, disabled, onTagAdd]
  );

  const handleRemove = useCallback(
    async (index: number) => {
      if (disabled) return;
      const removed = value[index];
      const newTags = value.filter((_, i) => i !== index);
      onChange(newTags);
      await onTagRemove?.(removed);
    },
    [value, onChange, disabled, onTagRemove]
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const inputId = id ?? "tag-input";

  return (
    <div className={`space-y-2 w-full ${className}`}>
      {label != null && (
        <Label htmlFor={inputId}>{label}</Label>
      )}
      <div
        className={`flex items-center gap-1.5 min-h-9 px-3 py-1 rounded-md border border-input bg-background text-sm w-full min-w-0 ${singleLine ? "flex-nowrap overflow-x-auto overflow-y-hidden" : "flex-wrap"}`}
        style={{ fontFamily: '"Figtree", sans-serif' }}
      >
        {value.map((tag, index) => (
          <Badge
            key={`${tag}-${index}`}
            variant="secondary"
            className={`flex items-center gap-1 pr-1 py-0.5 bg-secondary text-secondary-foreground border-0 [a&]:hover:opacity-90 ${singleLine ? "flex-shrink-0" : ""}`}
          >
            <span>{tag}</span>
            {!disabled && (
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                className="inline-flex items-center justify-center rounded-full p-0.5 opacity-80 hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-currentColor"
                onClick={() => handleRemove(index)}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        <Input
          id={inputId}
          type="text"
          disabled={disabled}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="border-0 p-0 h-7 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 flex-1 min-w-[120px]"
        />
      </div>
    </div>
  );
}

export default TagInput;
